import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils';

const AnalyticsPanel = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const [demographicData, setDemographicData] = useState([
    { location: 'California', users: 2845, vendors: 189, percentage: 23 },
    { location: 'New York', users: 2156, vendors: 145, percentage: 18 },
    { location: 'Texas', users: 1923, vendors: 134, percentage: 15 },
    { location: 'Florida', users: 1654, vendors: 98, percentage: 13 },
    { location: 'Illinois', users: 1432, vendors: 87, percentage: 12 }
  ]);

  const [categoryData, setCategoryData] = useState([
    { category: 'Photography', bookings: 89, revenue: 45600, growth: 15.2 },
    { category: 'Catering', bookings: 67, revenue: 78900, growth: 8.7 },
    { category: 'Decoration', bookings: 56, revenue: 34200, growth: 22.1 },
    { category: 'Henna/Mehndi', bookings: 43, revenue: 12800, growth: 18.5 },
    { category: 'Music/DJ', bookings: 38, revenue: 28400, growth: 12.3 }
  ]);

  const [userEngagementData, setUserEngagementData] = useState({
    avgSessionTime: '8m 34s',
    bounceRate: 24.5,
    pageViews: 156789,
    uniqueVisitors: 12450,
    conversionRate: 3.8,
    searchQueries: 8934
  });

  const [topSearchQueries, setTopSearchQueries] = useState([
    { query: 'wedding photographers near me', count: 2847 },
    { query: 'indian wedding catering', count: 1923 },
    { query: 'punjabi wedding decorations', count: 1654 },
    { query: 'henna artists bay area', count: 1432 },
    { query: 'south asian wedding venues', count: 1298 }
  ]);

  const periods = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const getGrowthColor = (growth) => {
    return growth > 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          Analytics Dashboard
        </h2>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
          >
            {periods.map((period) => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </select>
          <Button variant="primary" iconName="Download">
            Export Analytics
          </Button>
        </div>
      </div>

      {/* User Engagement Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Avg Session Time</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {userEngagementData.avgSessionTime}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Bounce Rate</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {userEngagementData.bounceRate}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingDown" size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Conversion Rate</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {userEngagementData.conversionRate}%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Target" size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Geographic Distribution
        </h3>
        
        <div className="space-y-4">
          {demographicData.map((location, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>
                <div>
                  <div className="font-medium text-text-primary">{location.location}</div>
                  <div className="text-sm text-text-secondary">
                    {location.users.toLocaleString()} users • {location.vendors} vendors
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="w-32 bg-surface-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${location.percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-text-secondary w-10 text-right">
                  {location.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Performance */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-border-light">
          <h3 className="font-heading font-semibold text-lg text-text-primary">
            Category Performance
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Growth
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border-light">
              {categoryData.map((category, index) => (
                <tr key={index} className="hover:bg-surface-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-text-primary">{category.category}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {category.bookings}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                    {formatPrice(category.revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getGrowthColor(category.growth)}`}>
                      {category.growth > 0 ? '+' : ''}{category.growth}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Search Queries */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Top Search Queries
        </h3>
        
        <div className="space-y-3">
          {topSearchQueries.map((query, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-surface-100 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{index + 1}</span>
                </div>
                <div>
                  <div className="font-medium text-text-primary">{query.query}</div>
                </div>
              </div>
              <div className="text-sm text-text-secondary">
                {query.count.toLocaleString()} searches
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Traffic Sources */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
            Traffic Sources
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Search" size={20} className="text-blue-600" />
                <span className="text-text-primary">Organic Search</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full w-3/4"></div>
                </div>
                <span className="text-sm text-text-secondary">45%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Link" size={20} className="text-green-600" />
                <span className="text-text-primary">Direct</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full w-1/2"></div>
                </div>
                <span className="text-sm text-text-secondary">28%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Share" size={20} className="text-purple-600" />
                <span className="text-text-primary">Social Media</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full w-1/4"></div>
                </div>
                <span className="text-sm text-text-secondary">18%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={20} className="text-orange-600" />
                <span className="text-text-primary">Email</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-orange-600 h-2 rounded-full w-1/5"></div>
                </div>
                <span className="text-sm text-text-secondary">9%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
            Device Usage
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Smartphone" size={20} className="text-blue-600" />
                <span className="text-text-primary">Mobile</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full w-3/5"></div>
                </div>
                <span className="text-sm text-text-secondary">62%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Monitor" size={20} className="text-green-600" />
                <span className="text-text-primary">Desktop</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full w-1/3"></div>
                </div>
                <span className="text-sm text-text-secondary">31%</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Tablet" size={20} className="text-purple-600" />
                <span className="text-text-primary">Tablet</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-20 bg-surface-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full w-1/6"></div>
                </div>
                <span className="text-sm text-text-secondary">7%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;